import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { 
  Users, Clock, TrendingUp, Calendar, LogIn, LogOut, AlertCircle, Loader2, Building2, Copy,
  Settings, Mail, CheckCircle, Send, Download, FileSpreadsheet, User, CalendarDays, Plus, 
  Trash2, Save, Shield, Bell, Database, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Settings states
  const [settings, setSettings] = useState(null);
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: "",
    address: "",
    phone: "",
    billing_email: ""
  });
  const [savingCompany, setSavingCompany] = useState(false);
  
  const [formData, setFormData] = useState({
    admin_email: "",
    work_start_time: "09:00",
    work_end_time: "18:00",
    standard_work_hours: 8
  });
  const [saving, setSaving] = useState(false);
  
  // Profile state
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Calendar state
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarData, setCalendarData] = useState({ holidays: [], company_holidays: [] });
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [savingCalendar, setSavingCalendar] = useState(false);
  
  // Month end
  const [monthEndData, setMonthEndData] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    send_email: true
  });
  const [processingMonthEnd, setProcessingMonthEnd] = useState(false);
  const [downloadingTimecard, setDownloadingTimecard] = useState(false);

  // Collapsed states for widgets
  const [expandedWidgets, setExpandedWidgets] = useState({
    company: false,
    gmail: false,
    settings: false,
    profile: false,
    monthEnd: false,
    calendar: false,
    leaveRequests: true
  });

  // Leave requests state
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loadingLeave, setLoadingLeave] = useState(false);
  const [processingLeave, setProcessingLeave] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, requestId: null, reason: '' });

  const toggleWidget = (widget) => {
    setExpandedWidgets(prev => ({ ...prev, [widget]: !prev[widget] }));
  };

  useEffect(() => {
    fetchStats();
    fetchCompanyInfo();
    fetchSettings();
    fetchProfile();
    fetchCalendar(calendarYear);
    fetchLeaveRequests();
  }, []);

  useEffect(() => {
    fetchCalendar(calendarYear);
  }, [calendarYear]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`);
      setStats(res.data);
    } catch (error) {
      toast.error("統計情報の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setCompanyInfo(res.data.company);
      setCompanyForm({
        name: res.data.company?.name || "",
        address: res.data.company?.address || "",
        phone: res.data.company?.phone || "",
        billing_email: res.data.company?.billing_email || ""
      });
    } catch (error) {
      console.error("Company info fetch error:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
      setFormData({
        admin_email: res.data.admin_email || "",
        work_start_time: res.data.work_start_time || "09:00",
        work_end_time: res.data.work_end_time || "18:00",
        standard_work_hours: res.data.standard_work_hours || 8
      });
    } catch (error) {
      console.error("Settings fetch error:", error);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/profile`);
      setProfileData(prev => ({
        ...prev,
        name: res.data.name || "",
        email: res.data.email || ""
      }));
    } catch (error) {
      console.error("Profile fetch error:", error);
    }
  };

  const fetchCalendar = async (year) => {
    setLoadingCalendar(true);
    try {
      const res = await axios.get(`${API}/calendar/${year}`);
      setCalendarData({
        holidays: res.data.holidays || [],
        company_holidays: res.data.company_holidays || []
      });
    } catch (error) {
      setCalendarData({ holidays: [], company_holidays: [] });
    } finally {
      setLoadingCalendar(false);
    }
  };

  const saveCompanyInfo = async () => {
    setSavingCompany(true);
    try {
      await axios.put(`${API}/company`, companyForm);
      toast.success("企業情報を更新しました");
      setEditingCompany(false);
      fetchCompanyInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || "更新に失敗しました");
    } finally {
      setSavingCompany(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, formData);
      toast.success("設定を保存しました");
      fetchSettings();
    } catch (error) {
      toast.error("設定の保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    if (profileData.new_password && profileData.new_password !== profileData.confirm_password) {
      toast.error("新しいパスワードが一致しません");
      return;
    }
    setSavingProfile(true);
    try {
      const payload = { name: profileData.name };
      if (profileData.new_password && profileData.current_password) {
        payload.current_password = profileData.current_password;
        payload.new_password = profileData.new_password;
      }
      await axios.put(`${API}/profile`, payload);
      toast.success("プロフィールを更新しました");
      setProfileData(prev => ({ ...prev, current_password: "", new_password: "", confirm_password: "" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "更新に失敗しました");
    } finally {
      setSavingProfile(false);
    }
  };

  const connectGmail = async () => {
    try {
      const res = await axios.get(`${API}/oauth/gmail/login`);
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error("Gmail連携の開始に失敗しました");
    }
  };

  const processMonthEnd = async () => {
    if (!settings?.gmail_connected && monthEndData.send_email) {
      toast.error("メール送信するにはGmail連携が必要です");
      return;
    }
    setProcessingMonthEnd(true);
    try {
      const res = await axios.post(`${API}/month-end`, monthEndData);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "月末処理に失敗しました");
    } finally {
      setProcessingMonthEnd(false);
    }
  };

  const downloadTimecardCSV = async () => {
    setDownloadingTimecard(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/attendance/export-timecard?year=${monthEndData.year}&month=${monthEndData.month}`,
        { responseType: "blob", headers: { Authorization: `Bearer ${token}` } }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `timecard_${monthEndData.year}_${String(monthEndData.month).padStart(2, "0")}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("タイムカードCSVをダウンロードしました");
    } catch (error) {
      toast.error("ダウンロードに失敗しました");
    } finally {
      setDownloadingTimecard(false);
    }
  };

  const saveCalendar = async () => {
    setSavingCalendar(true);
    try {
      await axios.put(`${API}/calendar/${calendarYear}`, calendarData);
      toast.success("カレンダーを保存しました");
    } catch (error) {
      toast.error("保存に失敗しました");
    } finally {
      setSavingCalendar(false);
    }
  };

  // Leave request functions
  const fetchLeaveRequests = async () => {
    setLoadingLeave(true);
    try {
      const res = await axios.get(`${API}/leave/requests`);
      setLeaveRequests(res.data);
    } catch (error) {
      console.error("Leave requests fetch error:", error);
    } finally {
      setLoadingLeave(false);
    }
  };

  const handleLeaveAction = async (requestId, action, rejectedReason = null) => {
    setProcessingLeave(requestId);
    try {
      await axios.put(`${API}/leave/requests/${requestId}`, {
        action,
        rejected_reason: rejectedReason
      });
      toast.success(action === 'approve' ? '承認しました' : '却下しました');
      fetchLeaveRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || '処理に失敗しました');
    } finally {
      setProcessingLeave(null);
    }
  };

  const formatLeaveDate = (startDate, endDate, halfDay) => {
    if (startDate === endDate) {
      const date = new Date(startDate);
      const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
      let text = `${date.getMonth() + 1}/${date.getDate()} (${dayNames[date.getDay()]})`;
      if (halfDay === 'am') text += ' 午前半休';
      if (halfDay === 'pm') text += ' 午後半休';
      return text;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.getMonth() + 1}/${start.getDate()} 〜 ${end.getMonth() + 1}/${end.getDate()}`;
  };

  // Calendar helpers
  const getJapaneseHolidays = (year) => {
    return [
      { date: `${year}-01-01`, name: "元日" },
      { date: `${year}-01-13`, name: "成人の日" },
      { date: `${year}-02-11`, name: "建国記念の日" },
      { date: `${year}-02-23`, name: "天皇誕生日" },
      { date: `${year}-03-20`, name: "春分の日" },
      { date: `${year}-04-29`, name: "昭和の日" },
      { date: `${year}-05-03`, name: "憲法記念日" },
      { date: `${year}-05-04`, name: "みどりの日" },
      { date: `${year}-05-05`, name: "こどもの日" },
      { date: `${year}-07-21`, name: "海の日" },
      { date: `${year}-08-11`, name: "山の日" },
      { date: `${year}-09-16`, name: "敬老の日" },
      { date: `${year}-09-22`, name: "秋分の日" },
      { date: `${year}-10-14`, name: "スポーツの日" },
      { date: `${year}-11-03`, name: "文化の日" },
      { date: `${year}-11-23`, name: "勤労感謝の日" }
    ];
  };

  const addAllJapaneseHolidays = () => {
    const holidays = getJapaneseHolidays(calendarYear);
    const existingDates = calendarData.holidays.map(h => h.date);
    const newHolidays = holidays.filter(h => !existingDates.includes(h.date));
    if (newHolidays.length === 0) {
      toast.info("すべての祝日は既に追加されています");
      return;
    }
    setCalendarData(prev => ({ ...prev, holidays: [...prev.holidays, ...newHolidays] }));
    toast.success(`${newHolidays.length}件の祝日を追加しました`);
  };

  const addAllWeekends = () => {
    const weekends = [];
    const year = calendarYear;
    const existingDates = calendarData.company_holidays.map(h => h.date);
    for (let month = 0; month < 12; month++) {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dayOfWeek = date.getDay();
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          if (!existingDates.includes(dateStr)) {
            weekends.push({ date: dateStr, name: dayOfWeek === 0 ? "日曜日" : "土曜日" });
          }
        }
      }
    }
    if (weekends.length === 0) {
      toast.info("すべての土日は既に追加されています");
      return;
    }
    setCalendarData(prev => ({ ...prev, company_holidays: [...prev.company_holidays, ...weekends] }));
    toast.success(`${weekends.length}件の土日を追加しました`);
  };

  const handleDateClick = (dayInfo) => {
    if (!dayInfo) return;
    if (dayInfo.isHoliday) {
      setCalendarData(prev => ({
        ...prev,
        holidays: prev.holidays.filter(h => h.date !== dayInfo.dateStr),
        company_holidays: prev.company_holidays.filter(h => h.date !== dayInfo.dateStr)
      }));
      toast.success(`${new Date(dayInfo.dateStr).toLocaleDateString('ja-JP')}の休日を解除しました`);
    } else {
      const date = new Date(dayInfo.dateStr);
      const dayOfWeek = date.getDay();
      let holidayName = "休業日";
      if (dayOfWeek === 0) holidayName = "日曜日";
      else if (dayOfWeek === 6) holidayName = "土曜日";
      setCalendarData(prev => ({
        ...prev,
        company_holidays: [...prev.company_holidays, { date: dayInfo.dateStr, name: holidayName }]
      }));
      toast.success(`${new Date(dayInfo.dateStr).toLocaleDateString('ja-JP')}を休日に設定しました`);
    }
  };

  const getMonthCalendar = (month) => {
    const year = calendarYear;
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const date = new Date(year, month, day);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const nationalHoliday = calendarData.holidays.find(h => h.date === dateStr);
      const companyHoliday = calendarData.company_holidays.find(h => h.date === dateStr);
      const isHoliday = !!nationalHoliday || !!companyHoliday;
      days.push({ day, dateStr, isWeekend, isHoliday, holidayName: nationalHoliday?.name || companyHoliday?.name || null });
    }
    return days;
  };

  const copyCompanyCode = () => {
    if (companyInfo?.code) {
      navigator.clipboard.writeText(companyInfo.code);
      toast.success("企業コードをコピーしました");
    }
  };

  const copyFaceAuthUrl = () => {
    if (companyInfo?.code) {
      const url = `${window.location.origin}/face-auth?company=${companyInfo.code}`;
      navigator.clipboard.writeText(url);
      toast.success("認証URLをコピーしました");
    }
  };

  const copyStaffLeaveUrl = () => {
    if (companyInfo?.code) {
      const url = `${window.location.origin}/staff-leave?company=${companyInfo.code}`;
      navigator.clipboard.writeText(url);
      toast.success("有給申請URLをコピーしました");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold mb-1">ダッシュボード</h1>
          <p className="text-slate-400 text-sm">本日: {stats?.today?.date || new Date().toISOString().split('T')[0]}</p>
        </div>
        {/* Company Code Badge */}
        {companyInfo?.code && (
          <div 
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg cursor-pointer hover:from-emerald-500 hover:to-teal-500 transition-all"
            onClick={copyCompanyCode}
            title="クリックでコピー"
            data-testid="company-code-badge"
          >
            <Building2 className="w-4 h-4 text-white" />
            <span className="text-white font-bold tracking-wider">{companyInfo.code}</span>
            <Copy className="w-3 h-3 text-white/70" />
          </div>
        )}
      </div>

      {/* Today's Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-emerald-900/30 border-emerald-700/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-400 uppercase mb-1">出勤中</p>
                <p className="text-3xl font-bold text-emerald-400">{stats?.today?.clocked_in || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <LogIn className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-900/30 border-blue-700/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-400 uppercase mb-1">退勤済</p>
                <p className="text-3xl font-bold text-blue-400">{stats?.today?.clocked_out || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <LogOut className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-yellow-900/30 border-yellow-700/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-yellow-400 uppercase mb-1">未出勤</p>
                <p className="text-3xl font-bold text-yellow-400">{stats?.today?.not_arrived || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase mb-1">総従業員</p>
                <p className="text-3xl font-bold">{stats?.total_employees || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                <Users className="w-5 h-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Summary & Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="w-4 h-4" />
              今月のサマリー ({new Date().getFullYear()}年{new Date().getMonth() + 1}月)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-300">総労働時間</span>
              </div>
              <span className="text-xl font-bold">{stats?.month_summary?.total_work_hours || 0}h</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-red-400" />
                <span className="text-sm text-slate-300">総残業時間</span>
              </div>
              <span className="text-xl font-bold text-red-400">{stats?.month_summary?.total_overtime_hours || 0}h</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-300">記録件数</span>
              </div>
              <span className="text-xl font-bold">{stats?.month_summary?.record_count || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">クイックアクション</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <a href="/admin/employees" className="flex items-center justify-between p-3 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span className="text-sm">従業員管理</span>
              </div>
              <span className="text-slate-400">→</span>
            </a>
            <a href="/admin/attendance" className="flex items-center justify-between p-3 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span className="text-sm">勤怠記録</span>
              </div>
              <span className="text-slate-400">→</span>
            </a>
            <a href="/admin/shifts" className="flex items-center justify-between p-3 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">シフト管理</span>
              </div>
              <span className="text-slate-400">→</span>
            </a>
            <a href="/face-auth" className="flex items-center justify-between p-3 rounded-lg bg-emerald-700 hover:bg-emerald-600 transition-colors">
              <div className="flex items-center gap-2">
                <LogIn className="w-4 h-4" />
                <span className="text-sm">認証画面</span>
              </div>
              <span className="text-slate-300">→</span>
            </a>
          </CardContent>
        </Card>
      </div>

      {/* Leave Requests Management */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader 
          className="pb-3 cursor-pointer hover:bg-slate-700/50 transition-colors"
          onClick={() => toggleWidget('leaveRequests')}
        >
          <CardTitle className="flex items-center justify-between text-base">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-emerald-400" />
              有給申請管理
              {leaveRequests.filter(r => r.status === 'pending').length > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {leaveRequests.filter(r => r.status === 'pending').length}件
                </Badge>
              )}
            </div>
            {expandedWidgets.leaveRequests ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </CardTitle>
        </CardHeader>
        {expandedWidgets.leaveRequests && (
          <CardContent>
            {loadingLeave ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : leaveRequests.length === 0 ? (
              <p className="text-center text-slate-400 py-8">有給申請はありません</p>
            ) : (
              <div className="space-y-3">
                {/* Pending requests first */}
                {leaveRequests.filter(r => r.status === 'pending').length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-yellow-400 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      承認待ち
                    </h4>
                    {leaveRequests.filter(r => r.status === 'pending').map(req => (
                      <div key={req.id} className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{req.employee_name}</p>
                            <p className="text-sm text-slate-300">
                              {formatLeaveDate(req.start_date, req.end_date, req.half_day)}
                              <span className="ml-2 text-slate-400">({req.days}日)</span>
                            </p>
                            {req.reason && <p className="text-xs text-slate-400 mt-1">理由: {req.reason}</p>}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleLeaveAction(req.id, 'approve'); }}
                              disabled={processingLeave === req.id}
                              className="bg-emerald-600 hover:bg-emerald-500"
                            >
                              {processingLeave === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRejectModal({ open: true, requestId: req.id, reason: '', employeeName: req.employee_name });
                              }}
                              disabled={processingLeave === req.id}
                            >
                              却下
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Approved/Rejected requests */}
                {leaveRequests.filter(r => r.status !== 'pending').length > 0 && (
                  <div className="space-y-2 mt-4">
                    <h4 className="text-sm font-semibold text-slate-400">処理済み</h4>
                    {leaveRequests.filter(r => r.status !== 'pending').slice(0, 5).map(req => (
                      <div key={req.id} className={`p-3 rounded-lg ${req.status === 'approved' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{req.employee_name}</p>
                            <p className="text-sm text-slate-300">
                              {formatLeaveDate(req.start_date, req.end_date, req.half_day)}
                              <span className="ml-2 text-slate-400">({req.days}日)</span>
                            </p>
                          </div>
                          <Badge variant={req.status === 'approved' ? 'default' : 'destructive'} className={req.status === 'approved' ? 'bg-emerald-600' : ''}>
                            {req.status === 'approved' ? '承認済' : '却下'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Collapsible Widget Cards */}
      <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* Company Info Card */}
        <Card className="border-emerald-700/50">
          <CardHeader 
            className="bg-emerald-900/30 rounded-t-lg pb-3 cursor-pointer hover:bg-emerald-900/40 transition-colors"
            onClick={() => toggleWidget('company')}
          >
            <CardTitle className="flex items-center justify-between text-base">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-400" />
                企業情報
              </div>
              {expandedWidgets.company ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </CardTitle>
          </CardHeader>
          {expandedWidgets.company && (
            <CardContent className="pt-4">
              {companyInfo ? (
                <div className="space-y-4">
                  {/* Company Code */}
                  <div className="p-4 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl text-white">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-emerald-100 text-xs mb-1">企業コード</p>
                        <p className="text-2xl font-bold tracking-wider">{companyInfo.code}</p>
                      </div>
                      <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); copyCompanyCode(); }} className="bg-white/20 hover:bg-white/30 text-white border-0">
                        <Copy className="w-4 h-4 mr-1" />コピー
                      </Button>
                    </div>
                  </div>
                  
                  {/* Face Auth URL */}
                  <div className="p-3 bg-slate-700 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-400 mb-1">認証URL</p>
                        <p className="text-xs text-slate-300 truncate">{window.location.origin}/face-auth?company={companyInfo.code}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); copyFaceAuthUrl(); }} className="ml-2 shrink-0">
                        <Copy className="w-3 h-3 mr-1" />コピー
                      </Button>
                    </div>
                  </div>

                  {/* Staff Leave Request URL */}
                  <div className="p-3 bg-slate-700 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-400 mb-1">有給申請URL（スタッフ用）</p>
                        <p className="text-xs text-slate-300 truncate">{window.location.origin}/staff-leave?company={companyInfo.code}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); copyStaffLeaveUrl(); }} className="ml-2 shrink-0">
                        <Copy className="w-3 h-3 mr-1" />コピー
                      </Button>
                    </div>
                  </div>

                  {/* Company Details */}
                  {editingCompany ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">企業名</Label>
                          <Input value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} className="mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs">請求先メール</Label>
                          <Input type="email" value={companyForm.billing_email} onChange={(e) => setCompanyForm({ ...companyForm, billing_email: e.target.value })} className="mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs">住所</Label>
                          <Input value={companyForm.address} onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })} className="mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs">電話番号</Label>
                          <Input value={companyForm.phone} onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })} className="mt-1" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={saveCompanyInfo} disabled={savingCompany} size="sm">
                          {savingCompany ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}保存
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setEditingCompany(false)}>キャンセル</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-slate-400">企業名:</span> {companyInfo.name}</div>
                        <div><span className="text-slate-400">電話:</span> {companyInfo.phone || "-"}</div>
                        <div><span className="text-slate-400">住所:</span> {companyInfo.address || "-"}</div>
                        <div><span className="text-slate-400">メール:</span> {companyInfo.billing_email || "-"}</div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Badge variant="outline">{companyInfo.plan_name || "未設定"}</Badge>
                        <Badge className={companyInfo.status === "active" ? "bg-emerald-600" : "bg-blue-600"}>
                          {companyInfo.status === "active" ? "有効" : companyInfo.status === "trial" ? "トライアル" : companyInfo.status}
                        </Badge>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setEditingCompany(true)} className="mt-2">編集</Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-20"><Loader2 className="w-6 h-6 animate-spin" /></div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Gmail Integration */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader 
            className="pb-3 cursor-pointer hover:bg-slate-700/50 transition-colors rounded-t-lg"
            onClick={() => toggleWidget('gmail')}
          >
            <CardTitle className="flex items-center justify-between text-base">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Gmail連携
              </div>
              {expandedWidgets.gmail ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </CardTitle>
          </CardHeader>
          {expandedWidgets.gmail && (
            <CardContent>
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700">
                {settings?.gmail_connected ? (
                  <>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                      <div>
                        <p className="text-sm font-medium">連携済み</p>
                        <p className="text-xs text-slate-400">{settings.gmail_email}</p>
                      </div>
                    </div>
                    <Badge className="bg-emerald-600">接続済</Badge>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-500" />
                      <div>
                        <p className="text-sm font-medium">未連携</p>
                        <p className="text-xs text-slate-400">メール送信には連携が必要</p>
                      </div>
                    </div>
                    <Button onClick={connectGmail} size="sm">連携する</Button>
                  </>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Basic Settings */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader 
            className="pb-3 cursor-pointer hover:bg-slate-700/50 transition-colors rounded-t-lg"
            onClick={() => toggleWidget('settings')}
          >
            <CardTitle className="flex items-center justify-between text-base">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                基本設定
              </div>
              {expandedWidgets.settings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </CardTitle>
          </CardHeader>
          {expandedWidgets.settings && (
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">管理者メール</Label>
                <Input value={formData.admin_email} onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })} placeholder="admin@example.com" className="mt-1" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">始業時刻</Label>
                  <Input type="time" value={formData.work_start_time} onChange={(e) => setFormData({ ...formData, work_start_time: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">終業時刻</Label>
                  <Input type="time" value={formData.work_end_time} onChange={(e) => setFormData({ ...formData, work_end_time: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">標準労働時間</Label>
                  <Input type="number" value={formData.standard_work_hours} onChange={(e) => setFormData({ ...formData, standard_work_hours: parseFloat(e.target.value) })} className="mt-1" />
                </div>
              </div>
              <Button onClick={saveSettings} disabled={saving} size="sm" className="w-full">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}設定を保存
              </Button>
            </CardContent>
          )}
        </Card>

        {/* Profile Settings */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader 
            className="pb-3 cursor-pointer hover:bg-slate-700/50 transition-colors rounded-t-lg"
            onClick={() => toggleWidget('profile')}
          >
            <CardTitle className="flex items-center justify-between text-base">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                管理者情報
              </div>
              {expandedWidgets.profile ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </CardTitle>
          </CardHeader>
          {expandedWidgets.profile && (
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">名前</Label>
                  <Input value={profileData.name} onChange={(e) => setProfileData({ ...profileData, name: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">メール</Label>
                  <Input value={profileData.email} disabled className="mt-1 bg-slate-700" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">現在のパスワード</Label>
                  <Input type="password" value={profileData.current_password} onChange={(e) => setProfileData({ ...profileData, current_password: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">新しいパスワード</Label>
                  <Input type="password" value={profileData.new_password} onChange={(e) => setProfileData({ ...profileData, new_password: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">パスワード確認</Label>
                  <Input type="password" value={profileData.confirm_password} onChange={(e) => setProfileData({ ...profileData, confirm_password: e.target.value })} className="mt-1" />
                </div>
              </div>
              <Button onClick={saveProfile} disabled={savingProfile} size="sm" className="w-full">
                {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}保存
              </Button>
            </CardContent>
          )}
        </Card>

        {/* Month End Processing */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader 
            className="pb-3 cursor-pointer hover:bg-slate-700/50 transition-colors rounded-t-lg"
            onClick={() => toggleWidget('monthEnd')}
          >
            <CardTitle className="flex items-center justify-between text-base">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                月末締め処理
              </div>
              {expandedWidgets.monthEnd ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </CardTitle>
          </CardHeader>
          {expandedWidgets.monthEnd && (
            <CardContent className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">年</Label>
                  <Select value={monthEndData.year.toString()} onValueChange={(v) => setMonthEndData({ ...monthEndData, year: parseInt(v) })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(year => <SelectItem key={year} value={year.toString()}>{year}年</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">月</Label>
                  <Select value={monthEndData.month.toString()} onValueChange={(v) => setMonthEndData({ ...monthEndData, month: parseInt(v) })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => <SelectItem key={i+1} value={(i+1).toString()}>{i+1}月</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">メール送信</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Switch checked={monthEndData.send_email} onCheckedChange={(v) => setMonthEndData({ ...monthEndData, send_email: v })} />
                    <span className="text-xs">{monthEndData.send_email ? "する" : "しない"}</span>
                  </div>
                </div>
              </div>
              <Button onClick={processMonthEnd} disabled={processingMonthEnd} size="sm" className="w-full">
                {processingMonthEnd ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                締め処理を実行
              </Button>
              <Button onClick={downloadTimecardCSV} disabled={downloadingTimecard} variant="outline" size="sm" className="w-full">
                {downloadingTimecard ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                タイムカードCSV
              </Button>
            </CardContent>
          )}
        </Card>

        {/* Calendar Card */}
        <Card className="bg-slate-800 border-slate-700 lg:col-span-2 xl:col-span-3">
          <CardHeader 
            className="pb-3 cursor-pointer hover:bg-slate-700/50 transition-colors rounded-t-lg"
            onClick={() => toggleWidget('calendar')}
          >
            <CardTitle className="flex items-center justify-between text-base">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4" />
                年間カレンダー
                <span className="text-xs text-slate-400 font-normal">クリックで休日設定/解除</span>
              </div>
              {expandedWidgets.calendar ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </CardTitle>
          </CardHeader>
          {expandedWidgets.calendar && (
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Select value={calendarYear.toString()} onValueChange={(v) => setCalendarYear(parseInt(v))}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(year => <SelectItem key={year} value={year.toString()}>{year}年</SelectItem>)}
                  </SelectContent>
                </Select>
                {loadingCalendar && <Loader2 className="w-4 h-4 animate-spin" />}
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <Button onClick={addAllJapaneseHolidays} variant="outline" size="sm">
                  <Calendar className="w-3 h-3 mr-1 text-red-500" />祝日一括追加
                </Button>
                <Button onClick={addAllWeekends} variant="outline" size="sm">
                  <CalendarDays className="w-3 h-3 mr-1 text-blue-500" />土日一括追加
                </Button>
                <Button onClick={() => { setCalendarData(prev => ({ ...prev, holidays: [] })); toast.success("祝日をクリア"); }} variant="outline" size="sm">
                  <Trash2 className="w-3 h-3 mr-1 text-red-500" />祝日クリア
                </Button>
                <Button onClick={() => { setCalendarData(prev => ({ ...prev, company_holidays: [] })); toast.success("休業日をクリア"); }} variant="outline" size="sm">
                  <Trash2 className="w-3 h-3 mr-1 text-blue-500" />休業日クリア
                </Button>
              </div>

              {/* Visual Calendar */}
              <div className="grid grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                {Array.from({ length: 12 }, (_, month) => (
                  <div key={month} className="border rounded-lg p-2 bg-slate-700">
                    <p className="text-xs font-medium text-center mb-1">{month + 1}月</p>
                    <div className="grid grid-cols-7 gap-px text-center">
                      {['日', '月', '火', '水', '木', '金', '土'].map(day => (
                        <div key={day} className={`text-[7px] ${day === '日' ? 'text-red-400' : day === '土' ? 'text-blue-400' : 'text-slate-500'}`}>{day}</div>
                      ))}
                      {getMonthCalendar(month).map((dayInfo, idx) => (
                        <div
                          key={idx}
                          onClick={(e) => { e.stopPropagation(); handleDateClick(dayInfo); }}
                          title={dayInfo?.holidayName || ''}
                          className={`text-[8px] h-4 flex items-center justify-center rounded-sm cursor-pointer select-none
                            ${!dayInfo ? '' : 
                              dayInfo.isHoliday ? 'bg-red-500 text-white font-bold hover:bg-red-600' :
                              dayInfo.isWeekend ? 'bg-blue-900/50 text-blue-400 hover:bg-blue-800/50' :
                              'text-slate-400 hover:bg-slate-600'
                            }`}
                        >
                          {dayInfo?.day || ''}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex gap-3 text-xs text-slate-400">
                  <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-500 rounded-sm"></div>休日</div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-900/50 rounded-sm"></div>土日</div>
                </div>
                <Button onClick={saveCalendar} disabled={savingCalendar} size="sm">
                  {savingCalendar ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  カレンダーを保存
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Reject Modal */}
      {rejectModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setRejectModal({ open: false, requestId: null, reason: '' })}>
          <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md mx-4 border border-slate-700" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">有給申請を却下</h3>
            <p className="text-sm text-slate-400 mb-4">{rejectModal.employeeName}さんの申請を却下します</p>
            <div className="mb-4">
              <Label className="text-sm text-slate-300">却下理由（任意）</Label>
              <Textarea
                value={rejectModal.reason}
                onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
                placeholder="例: 繁忙期のため、別日程での取得をお願いします"
                className="mt-2 bg-slate-700 border-slate-600"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setRejectModal({ open: false, requestId: null, reason: '' })}
                className="flex-1"
              >
                キャンセル
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  handleLeaveAction(rejectModal.requestId, 'reject', rejectModal.reason || null);
                  setRejectModal({ open: false, requestId: null, reason: '' });
                }}
                className="flex-1"
                disabled={processingLeave === rejectModal.requestId}
              >
                {processingLeave === rejectModal.requestId ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                却下する
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
